// ABOUTME: Select dropdown component using CDropdown for consistent styling
// ABOUTME: Provides both CDropdown-based select and compound Select with portal support

import * as React from "react"
import * as SelectPrimitive from "@radix-ui/react-select"
import { Check, ChevronDown, ChevronUp } from "lucide-react"
import { cn } from "@shared/lib/utils"
import { getCurrentZIndex, Z_INDEX_STEP } from "@shared/lib/zIndex"
import { CDropdown, CDropdownItem } from "./CDropdown"

// CSelect.Option compound component (data carrier, renders nothing)
interface CSelectOptionProps {
  value: string
  children: React.ReactNode
  /** Rich description shown below the label (text, badges, etc.) */
  description?: React.ReactNode
  /** Label shown in trigger when this option is selected */
  selectedLabel?: string
  disabled?: boolean
}

/** A function or class component (which may carry a displayName), as opposed to an intrinsic tag name */
const isComponent = (type: unknown): type is { displayName?: string } => typeof type === "function"

const CSelectOption: React.FC<CSelectOptionProps> = () => null
CSelectOption.displayName = "CSelectOption"

// CSelect component using CDropdown internally
type CSelectProps = Omit<React.SelectHTMLAttributes<HTMLSelectElement>, "size"> & {
  placeholderCustomStyle?: string
  placeholderCustom?: string
  sizing?: "xs" | "sm" | "md" | "lg"
  color?: "gray" | "info" | "failure" | "warning" | "success"
  helperText?: React.ReactNode
  /** Ghost variant shows as plain text, becomes select on parent group-hover */
  variant?: "default" | "ghost"
  /** Additional class for dropdown content container */
  contentClassName?: string
}

interface OptionData {
  value: string
  label: React.ReactNode
  /** Label shown when this option is selected (if different from dropdown label) */
  selectedLabel?: string
  /** Description shown below the label in dropdown */
  description?: React.ReactNode
  disabled?: boolean
  groupLabel?: string
}

interface OptionProps {
  value?: string | number
  children?: React.ReactNode
  disabled?: boolean
  label?: string
  /** Custom attribute: label to show when selected */
  "data-selected-label"?: string
  /** Custom attribute: description shown below the label */
  "data-description"?: string
}

// Recursively extract all <option> / <CSelect.Option> elements, including those inside <optgroup>
function extractOptions(children: React.ReactNode, groupLabel?: string): OptionData[] {
  const result: OptionData[] = []

  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) return

    const childType = child.type

    // CSelect.Option compound component
    if (childType === CSelectOption) {
      const props = child.props as CSelectOptionProps
      result.push({
        value: props.value,
        label: props.children,
        selectedLabel: props.selectedLabel,
        description: props.description,
        disabled: props.disabled,
        groupLabel,
      })
      return
    }

    const isOption = childType === "option" || (isComponent(childType) && childType.displayName === "option")
    const isOptgroup = childType === "optgroup"

    if (isOption) {
      const props = child.props as OptionProps
      result.push({
        value: String(props.value ?? ""),
        label: props.children,
        selectedLabel: props["data-selected-label"],
        description: props["data-description"],
        disabled: props.disabled,
        groupLabel,
      })
    } else if (isOptgroup) {
      const props = child.props as OptionProps
      const nestedOptions = extractOptions(props.children, props.label)
      result.push(...nestedOptions)
    }
  })

  return result
}

const CSelectComponent = React.forwardRef<HTMLDivElement, CSelectProps>((props, ref) => {
  const {
    className,
    placeholderCustomStyle,
    placeholderCustom,
    sizing = "sm",
    color,
    helperText,
    children,
    value,
    onChange,
    onBlur,
    disabled,
    variant = "default",
    contentClassName,
    ...rest
  } = props

  // Parse <option> and <optgroup> children to extract all options
  const options: OptionData[] = React.useMemo(() => {
    return extractOptions(children)
  }, [children])

  // Find selected option label (use selectedLabel if defined, otherwise label)
  const selectedOption = options.find((opt) => opt.value === String(value))
  // Don't use disabled placeholder option as fallback display
  const firstSelectableOption = options.find((opt) => !opt.disabled)
  const displayLabel = selectedOption?.selectedLabel || selectedOption?.label || firstSelectableOption?.label || ""

  // Check if any option has description (for dropdown width adjustment)
  const hasDescriptions = options.some((opt) => Boolean(opt.description))

  // Handle selection
  const handleSelect = (optionValue: string) => {
    if (!onChange) return
    // The one synthetic event both callbacks get: react-hook-form reads only target.name / target.value
    const syntheticEvent = {
      target: { value: optionValue, name: rest.name },
      currentTarget: { value: optionValue, name: rest.name },
    } as React.ChangeEvent<HTMLSelectElement> & React.FocusEvent<HTMLSelectElement>
    onChange(syntheticEvent)
    // Trigger onBlur after selection so react-hook-form revalidates (clears errors)
    onBlur?.(syntheticEvent)
  }

  // Size classes for trigger
  // Three heights, same as CButton: 30 for a service control in a card corner, 36 for the
  // desktop default, 44 for touch (docs/design/Дизайн-система.dc.html §04). 38 and 46 were
  // 36 and 44 plus the 1px border on each side — a leftover from before `box-sizing:
  // border-box`, and the reason a select never lined up with the button beside it.
  const sizeClasses = {
    xs: "h-[30px] text-xs",
    sm: "h-[30px] text-sm",
    // `md` is the standard control, so it grows to the 44px touch target on phones by itself —
    // "все нажимаемые элементы 44px" is a rule of the design, not of each call site.
    md: "h-9 max-md:h-11 text-sm",
    lg: "h-11 text-base",
  }

  // Color/border classes for trigger
  const colorClasses = {
    gray: "border-gray-300",
    info: "border-blue-500",
    failure: "border-red-500 text-red-900",
    warning: "border-yellow-500",
    success: "border-green-500",
  }

  const triggerColorClass = color ? colorClasses[color] : colorClasses.gray
  const hasError = color === "failure"

  const isGhost = variant === "ghost"

  return (
    <div
      ref={ref}
      className={cn("relative w-full", className)}
      data-field-error={hasError || undefined}
    >
      <CDropdown
        label={
          <div
            className={cn(
              "flex items-center justify-between w-full rounded-lg pl-2.5 pr-2 transition-all",
              sizeClasses[sizing],
              isGhost
                ? "border-transparent bg-transparent group-hover:border group-hover:bg-white group-hover:border-gray-300"
                : cn("bg-white border", triggerColorClass),
              disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer",
            )}
          >
            <span className="flex-1 text-left truncate">{displayLabel}</span>
            <ChevronDown
              className={cn(
                "h-5 w-5 shrink-0 text-gray-500 transition-opacity",
                isGhost && "opacity-0 group-hover:opacity-100",
              )}
            />
          </div>
        }
        disabled={disabled}
        placement="bottom-start"
        contentClassName={cn("min-w-[var(--trigger-width)]", hasDescriptions && "max-w-[320px]", contentClassName)}
        className="w-full"
        arrowIcon={false}
      >
        {options.map((opt, index) => {
          // First disabled option is a placeholder label, not a selectable item
          if (index === 0 && opt.disabled) {
            return (
              <div
                key={opt.value}
                className="px-4 py-1.5 text-xs text-gray-400 font-medium border-b border-gray-100 uppercase"
              >
                {opt.label}
              </div>
            )
          }
          return (
            <CDropdownItem
              key={opt.value}
              onClick={() => !opt.disabled && handleSelect(opt.value)}
              disabled={opt.disabled}
              className={cn(
                opt.description && "!py-2.5 !whitespace-normal",
                opt.value === String(value) && "bg-blue-50 text-blue-700",
              )}
            >
              {opt.description ? (
                <div className="flex flex-col min-w-0 text-left">
                  <span className="font-medium text-gray-900 break-words">{opt.label}</span>
                  <span className="text-xs text-gray-500 break-words">{opt.description}</span>
                </div>
              ) : (
                opt.label
              )}
            </CDropdownItem>
          )
        })}
      </CDropdown>
      {placeholderCustom && !selectedOption && (
        <span className={cn("absolute right-[50px] text-sm bottom-1.5", placeholderCustomStyle)}>
          {placeholderCustom}
        </span>
      )}
      {helperText && (
        <p className={cn("mt-1 text-sm", color === "failure" ? "text-red-600" : "text-gray-500")}>{helperText}</p>
      )}
    </div>
  )
})

CSelectComponent.displayName = "CSelect"

const CSelectWithStatics = Object.assign(CSelectComponent, {
  Option: CSelectOption,
})

// Exported under its own identifier: Fast Refresh (react-refresh/only-export-components)
// recognises a component by the name, and the Object.assign that attaches the statics is opaque to it.
export const CSelect = CSelectWithStatics

export default CSelect

// Radix UI Select components (complex use cases with portal)
const Select = SelectPrimitive.Root
const SelectGroup = SelectPrimitive.Group
const SelectValue = SelectPrimitive.Value

const SelectTrigger = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      "flex h-9 w-full items-center justify-between whitespace-nowrap rounded-md border border-gray-300 bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-white data-[placeholder]:text-gray-400 focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer [&>span]:line-clamp-1",
      className,
    )}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon asChild>
      <ChevronDown className="h-4 w-4 opacity-50" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
))
SelectTrigger.displayName = "SelectTrigger"

const SelectScrollUpButton = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn("flex cursor-default items-center justify-center py-1", className)}
    {...props}
  >
    <ChevronUp className="h-4 w-4" />
  </SelectPrimitive.ScrollUpButton>
))
SelectScrollUpButton.displayName = "SelectScrollUpButton"

const SelectScrollDownButton = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn("flex cursor-default items-center justify-center py-1", className)}
    {...props}
  >
    <ChevronDown className="h-4 w-4" />
  </SelectPrimitive.ScrollDownButton>
))
SelectScrollDownButton.displayName = "SelectScrollDownButton"

const SelectContent = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = "popper", style, ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        "relative max-h-[--radix-select-content-available-height] min-w-[8rem] overflow-y-auto overflow-x-hidden rounded-md border bg-white text-gray-900 shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-select-content-transform-origin]",
        position === "popper" &&
          "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
        className,
      )}
      style={{ zIndex: getCurrentZIndex() + Z_INDEX_STEP, ...style }}
      position={position}
      {...props}
    >
      <SelectScrollUpButton />
      <SelectPrimitive.Viewport
        className={cn(
          "p-1",
          position === "popper" &&
            "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]",
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
))
SelectContent.displayName = "SelectContent"

const SelectLabel = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn("px-2 py-1.5 text-sm font-semibold", className)}
    {...props}
  />
))
SelectLabel.displayName = "SelectLabel"

const SelectItem = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      "relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-2 pr-8 text-sm outline-none focus:bg-gray-100 focus:text-gray-900 data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed",
      className,
    )}
    {...props}
  >
    <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <Check className="h-4 w-4" />
      </SelectPrimitive.ItemIndicator>
    </span>
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
))
SelectItem.displayName = "SelectItem"

const SelectSeparator = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn("-mx-1 my-1 h-px bg-gray-200", className)}
    {...props}
  />
))
SelectSeparator.displayName = "SelectSeparator"

// Export Radix UI Select components
export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
}
